import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/UI/table";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from "@/components/UI/card";
import { Button } from "@/components/UI/button";
import { Input } from "@/components/UI/input";
import { Badge } from "@/components/UI/badge";
import { Switch } from "@/components/UI/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/UI/dialog";
// Make sure Edit is included in your imports from lucide-react
import { 
  ArrowLeft,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Search,
  FileSpreadsheet,
  DollarSign,
  Calendar,
  RefreshCw,
  Eye,
  EyeOff,
  Filter,
  Pencil,
  AlertTriangle,
  Edit  // Add this
} from 'lucide-react';
import { useToast } from "@/components/UI/use-toast";
import { misApi } from '@/services/misApi';

const MaterialsDetailView = ({ 
  category, 
  month, 
  onClose, 
  onDataUpdated 
}) => {
  const { toast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [accountDetails, setAccountDetails] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('collapsed'); // 'collapsed', 'expanded'
  const [hasDateIssues, setHasDateIssues] = useState(false);
  const [dateIssuesCount, setDateIssuesCount] = useState(0);
  const [fixDateDialogOpen, setFixDateDialogOpen] = useState(false);
  const [isFixingDates, setIsFixingDates] = useState(false);
  const [categoryAccounts, setCategoryAccounts] = useState([]);
  
  // Fetch account details when component mounts
  useEffect(() => {
    if (category && month) {
      fetchAccountDetails();
      fetchCategoryAccounts();
    }
  }, [category, month]);
  
  // Fetch the specific accounts associated with this category from the database
  const fetchCategoryAccounts = async () => {
    try {
      // First, get the category details which should include source_accounts
      const response = await misApi.getCategoryById(category.id);
      
      if (response.data && response.data.data) {
        const categoryData = response.data.data;
        
        // If the category has source_accounts stored, use those
        if (categoryData.source_accounts) {
          setCategoryAccounts(categoryData.source_accounts);
        } else {
          // Otherwise, fetch from monthly data which also stores source_accounts
          const monthlyDataResponse = await misApi.getCategoryMonthlyData(category.id, month);
          
          if (monthlyDataResponse.data && monthlyDataResponse.data.data && 
              monthlyDataResponse.data.data.source_accounts) {
            setCategoryAccounts(monthlyDataResponse.data.data.source_accounts);
          } else {
            // Fallback to known materials accounts if no data is available
            // This should only happen if there's no configuration yet
            console.warn("No source accounts found for category, using fallback accounts");
            setCategoryAccounts(['510101', '510109', '510110', '510201', '510206', '510207', '510208', '510301']);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching category accounts:", error);
      // If all else fails, use fallback accounts
      setCategoryAccounts(['510101', '510109', '510110', '510201', '510206', '510207', '510208', '510301']);
    }
  };
  
  // Fetch account details for the selected category
  const fetchAccountDetails = async () => {
    setLoading(true);
    try {
      console.log("Fetching account details with categoryId:", category.id, 
                  "Type:", typeof category.id, 
                  "Month:", month);
      
      // Check if ID is valid before making the API call
      if (!category.id) {
        console.error("Category ID is undefined or null");
        toast({
          variant: "destructive",
          title: "Error",
          description: "Category ID is missing. Please contact support.",
          duration: 3000,
        });
        setLoading(false);
        return;
      }
      
      const [startDate, endDate] = misApi.getMonthDateRange(month);
      
      // Fetch account details from API
      const response = await misApi.getCategoryAccountDetails(category.id, month);
      const data = response.data.data || [];
      setAccountDetails(data);
      
      // Check for date issues if we have account numbers
      // We'll do this after we have the category accounts
      checkForDateIssues(startDate);
      
      // Initialize expanded state
      const expandedState = {};
      data.forEach(account => {
        expandedState[account.Account] = false;
      });
      setExpandedAccounts(expandedState);
      
      setLoading(false);
    } catch (error) {
      console.error("Error details:", error.response?.data || error.message);
      console.error("Error fetching account details:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load account details. Please try again.",
        duration: 3000,
      });
      setLoading(false);
    }
  };
  
  // Check for date issues where transactions may be incorrectly dated
  const checkForDateIssues = async (startDate) => {
    if (!categoryAccounts || categoryAccounts.length === 0) {
      return; // Wait until we have account numbers
    }
    
    try {
      // Use the dynamic account list from the category
      const accountsQueryStr = categoryAccounts.join(',');
      
      // Check for transactions that were created in current month but dated in previous month
      const dateCheckResponse = await misApi.checkDateIssues(
        accountsQueryStr, 
        startDate
      );
      
      if (dateCheckResponse.data && dateCheckResponse.data.data) {
        const issueCount = dateCheckResponse.data.data.issueCount || 0;
        setHasDateIssues(issueCount > 0);
        setDateIssuesCount(issueCount);
        
        if (issueCount > 0) {
          console.warn(`Found ${issueCount} transactions with potential date issues`);
        }
      }
    } catch (error) {
      console.error("Error checking for date issues:", error);
      // Don't show an error toast here - this is a background check
    }
  };
  
  // Fix date issues
  const handleFixDateIssues = async () => {
    setIsFixingDates(true);
    try {
      // Call the API to fix date issues
      const response = await misApi.fixDateIssues(categoryAccounts);
      
      if (response.data && response.data.status === 'success') {
        const fixedCount = response.data.data?.fixedTransactions?.length || 0;
        
        toast({
          title: "Date Issues Fixed",
          description: `Successfully fixed ${fixedCount} transactions with incorrect dates.`,
          duration: 5000,
        });
        
        // Refresh data after fixing dates
        await fetchAccountDetails();
        
        // Notify parent component to refresh P&L data
        if (onDataUpdated) {
          onDataUpdated();
        }
        
        // Reset state
        setHasDateIssues(false);
        setDateIssuesCount(0);
        setFixDateDialogOpen(false);
      } else {
        throw new Error("Failed to fix date issues");
      }
    } catch (error) {
      console.error("Error fixing date issues:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fix date issues. Please try again.",
        duration: 3000,
      });
    } finally {
      setIsFixingDates(false);
    }
  };
  
  // Fetch transactions for a specific account
  const fetchAccountTransactions = async (account) => {
    try {
      setSelectedAccount(account);
      
      const [startDate, endDate] = misApi.getMonthDateRange(month);
      
      const response = await misApi.getAccountTransactions(account.Account, startDate, endDate);
      setTransactions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching account transactions:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load transaction details. Please try again.",
        duration: 3000,
      });
    }
  };
  
  // Toggle expanded state for an account
  const toggleAccountExpanded = async (account) => {
    const newExpandedState = {
      ...expandedAccounts,
      [account.Account]: !expandedAccounts[account.Account]
    };
    
    setExpandedAccounts(newExpandedState);
    
    // If expanding and no transactions loaded yet, fetch them
    if (newExpandedState[account.Account]) {
      await fetchAccountTransactions(account);
    }
  };
  
  // Handle refreshing the data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAccountDetails();
    setRefreshing(false);
    
    toast({
      title: "Data Refreshed",
      description: "Account details have been updated",
      duration: 2000,
    });
  };
  
  // Handle editing category value
  const handleEditValue = (value) => {
    setEditing(true);
    setEditedValue(value ? value.toString() : "0");
  };
  
  // Save edited value
  const handleSaveEdit = async () => {
    try {
      // Validate input is a number
      const numericValue = parseFloat(editedValue);
      if (isNaN(numericValue)) {
        toast({
          variant: "destructive",
          title: "Invalid Value",
          description: "Please enter a valid number",
          duration: 3000,
        });
        return;
      }
      
      // Prepare the update data
      const updateData = {
        category_id: category.id,
        month: month,
        mtd_actual: numericValue
      };
      
      // Show loading toast
      toast({
        title: "Updating...",
        description: "Saving changes to the database",
        duration: 2000,
      });
      
      // Call API to update the value
      await misApi.updateProfitLossData(updateData);
      
      toast({
        title: "Value Updated",
        description: `${category.name} value has been updated to AED ${formatCurrency(numericValue)}`,
        duration: 3000,
      });
      
      // Reset editing state
      setEditing(false);
      setEditedValue(null);
      
      // Refresh data to ensure consistency
      await fetchAccountDetails();
      
      // Notify parent component to refresh data
      if (onDataUpdated) {
        onDataUpdated();
      }
    } catch (error) {
      console.error("Error updating value:", error);
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: error.response?.data?.message || "Failed to update value. Please try again.",
        duration: 3000,
      });
    }
  };
  
  // Cancel editing
  const handleCancelEdit = () => {
    setEditing(false);
    setEditedValue(null);
  };
  
  // Format currency values
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "-";
    
    return new Intl.NumberFormat('en-AE', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };
  
  // Filter accounts based on search term
  const filteredAccounts = accountDetails.filter(account => 
    account.Name && account.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.Account && account.Account.toString().includes(searchTerm)
  );
  
  // Calculate totals - Ensure values are numeric before calculations
  const totalCredit = accountDetails.reduce((sum, account) => sum + (parseFloat(account.total_credit) || 0), 0);
  const totalDebit = accountDetails.reduce((sum, account) => sum + (parseFloat(account.total_debit) || 0), 0);
  const netTotal = totalCredit - totalDebit;
  
  // Loading skeleton
  if (loading) {
    return (
      <Card className="w-full shadow-md">
        <CardHeader className="pb-2 bg-gradient-to-r from-blue-600 to-teal-500 text-white">
          <div className="flex justify-between items-center">
            <div className="h-6 w-48 bg-white/20 animate-pulse rounded"></div>
            <Button size="sm" variant="ghost" className="h-8 w-8 text-white">
              <X size={16} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 p-2">
            <div className="h-10 w-full bg-gray-200 animate-pulse rounded"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="grid grid-cols-4 gap-4">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="h-8 bg-gray-200 animate-pulse rounded"></div>
                ))}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="w-full shadow-md">
      <CardHeader className="bg-gradient-to-r from-blue-600 to-teal-500 text-white py-3 px-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="sm" 
              className="mr-2 text-white hover:bg-white/20" 
              onClick={onClose}
            >
              <ArrowLeft size={16} />
            </Button>
            <div>
              <CardTitle className="text-lg font-semibold flex items-center">
                {category.name} Details
                <Badge variant="outline" className="ml-3 bg-white/20 text-white border-white/30">
                  {month}
                </Badge>
              </CardTitle>
              <CardDescription className="text-white/80 text-sm mt-1">
                Financial breakdown of materials accounts and transactions
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center">
            <div className="mr-4 opacity-20">
              <img src="/src/assets/PhoenicianLogo.png" alt="Phoenician Technical Services" className="h-8" />
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-white/20"
              onClick={onClose}
            >
              <X size={16} />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {/* Date Issue Warning Banner */}
        {hasDateIssues && (
          <div className="bg-yellow-100 border-b border-yellow-300 p-3 flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Potential Date Issues Detected</h3>
                <p className="text-xs text-yellow-700 mt-1">
                  Found {dateIssuesCount} transaction(s) that may have incorrect dates. 
                  This can affect your financial reports.
                </p>
              </div>
            </div>
            <Button 
              size="sm" 
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
              onClick={() => setFixDateDialogOpen(true)}
            >
              Fix Issues
            </Button>
          </div>
        )}
      
        {/* Category Value Editing */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-b shadow-sm">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center p-4">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg font-medium text-gray-800 flex items-center">
                <DollarSign className="h-5 w-5 mr-1 text-blue-600" />
                MTD Total
              </h3>
              {editing ? (
                <div className="flex items-center mt-2">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">AED</span>
                    </div>
                    <Input 
                      type="text"
                      className="w-48 mr-2 pl-14 pr-3 py-2 border-blue-300 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      value={editedValue}
                      onChange={(e) => setEditedValue(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <Button 
                    size="sm" 
                    variant="default" 
                    className="mr-1 bg-green-600 hover:bg-green-700 text-white transition-colors"
                    onClick={handleSaveEdit}
                  >
                    <Save size={14} className="mr-1" />
                    Save
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={handleCancelEdit}
                  >
                    <X size={14} className="mr-1" />
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center mt-2">
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold text-blue-800 flex items-baseline">
                      AED <span className="ml-1">{!isNaN(netTotal) ? formatCurrency(netTotal) : "0.00"}</span>
                    </span>
                    <span className="text-xs text-blue-600 mt-1">
                      {month} • Category: {category.name}
                    </span>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="ml-3 border-blue-300 text-blue-600 hover:bg-blue-100 hover:border-blue-400"
                    onClick={() => handleEditValue(netTotal || 0)}
                  >
                    <Edit size={14} className="mr-1" />
                    Edit Value
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded-md shadow-sm border-l-4 border-green-500">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500 flex items-center">
                    <span className="inline-block h-2 w-2 rounded-full bg-green-500 mr-2"></span>
                    Total Credits
                  </p>
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-200">Credit</Badge>
                </div>
                <p className="text-lg font-semibold text-green-600 mt-1">AED {formatCurrency(totalCredit)}</p>
              </div>
              <div className="bg-white p-3 rounded-md shadow-sm border-l-4 border-red-500">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500 flex items-center">
                    <span className="inline-block h-2 w-2 rounded-full bg-red-500 mr-2"></span>
                    Total Debits
                  </p>
                  <Badge className="bg-red-100 text-red-800 hover:bg-red-200">Debit</Badge>
                </div>
                <p className="text-lg font-semibold text-red-600 mt-1">AED {formatCurrency(totalDebit)}</p>
              </div>
            </div>
          </div>
          
          {/* Progress bar visualizing credit vs debit */}
          {totalCredit + totalDebit > 0 && (
            <div className="h-2 w-full flex">
              <div 
                className="bg-green-500" 
                style={{ width: `${(totalCredit / (totalCredit + totalDebit)) * 100}%` }}
              ></div>
              <div 
                className="bg-red-500" 
                style={{ width: `${(totalDebit / (totalCredit + totalDebit)) * 100}%` }}
              ></div>
            </div>
          )}
        </div>
        
        {/* Display category accounts */}
        <div className="p-3 bg-blue-50 border-b border-blue-200">
          <h4 className="text-sm font-medium text-blue-800 mb-1">Material Accounts:</h4>
          <div className="flex flex-wrap gap-1">
            {categoryAccounts.map((acct) => (
              <Badge 
                key={acct} 
                variant="outline" 
                className="bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200"
              >
                {acct}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-blue-700 mt-2">
            These accounts are specifically configured to be part of the Materials category
          </p>
        </div>
        
        {/* Action bar with search, view options, and refresh */}
        <div className="p-3 bg-gray-50 flex flex-col sm:flex-row justify-between items-center border-b">
          <div className="flex items-center space-x-2 w-full sm:w-64 mb-2 sm:mb-0">
            <Search className="h-4 w-4 text-gray-500" />
            <Input 
              placeholder="Search material accounts..." 
              className="h-8 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex space-x-2 items-center">
            <div className="flex items-center mr-2 bg-white px-2 py-1 rounded border">
              <div className="flex items-center mr-3">
                <span className="text-xs text-gray-500 mr-2">View Mode:</span>
                <Switch 
                  checked={viewMode === 'expanded'} 
                  onCheckedChange={(checked) => setViewMode(checked ? 'expanded' : 'collapsed')}
                  size="sm"
                />
              </div>
              <span className="text-xs font-medium">
                {viewMode === 'expanded' ? (
                  <span className="flex items-center text-blue-600">
                    <Eye size={12} className="mr-1" />
                    Show All
                  </span>
                ) : (
                  <span className="flex items-center text-gray-600">
                    <EyeOff size={12} className="mr-1" />
                    Collapsed
                  </span>
                )}
              </span>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              className="flex items-center h-8 text-sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              size="sm"
              variant="outline"
              className="flex items-center h-8 text-sm"
              disabled={true} // Enable when implemented
            >
              <Filter className="h-4 w-4 mr-1" />
              Filter
            </Button>
          </div>
        </div>
        
        {/* Accounts table */}
        <div className="overflow-auto max-h-[70vh]">
          {filteredAccounts.length > 0 ? (
            <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16 bg-blue-600 text-white font-semibold text-xs p-2">Account</TableHead>
                  <TableHead className="bg-blue-600 text-white font-semibold text-xs p-2">Name</TableHead>
                  <TableHead className="text-right bg-blue-600 text-white font-semibold text-xs p-2 w-24">Credit (AED)</TableHead>
                  <TableHead className="text-right bg-blue-600 text-white font-semibold text-xs p-2 w-24">Debit (AED)</TableHead>
                  <TableHead className="text-right bg-blue-600 text-white font-semibold text-xs p-2 w-24">Net (AED)</TableHead>
                  <TableHead className="w-10 text-center bg-blue-600 text-white font-semibold text-xs p-2">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <React.Fragment key={account.Account}>
                    <TableRow 
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => toggleAccountExpanded(account)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell className="font-medium text-blue-700 text-xs p-2">{account.Account}</TableCell>
                      <TableCell className="font-medium text-xs p-2 truncate max-w-sm">{account.Name}</TableCell>
                      <TableCell className="text-right text-green-600 font-medium text-xs p-2">
                        <span className="bg-green-50 px-1 py-0.5 rounded">
                          {formatCurrency(account.total_credit)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-red-600 font-medium text-xs p-2">
                        <span className="bg-red-50 px-1 py-0.5 rounded">
                          {formatCurrency(account.total_debit)}
                        </span>
                      </TableCell>
                      <TableCell 
                        className="text-right font-semibold text-xs p-2"
                      >
                        <span className={`px-1 py-0.5 rounded ${
                          (account.total_credit - account.total_debit) >= 0 
                            ? 'bg-blue-50 text-blue-700' 
                            : 'bg-red-50 text-red-700'
                        }`}>
                          {formatCurrency(account.total_credit - account.total_debit)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center p-1">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className={`h-6 w-6 rounded-full p-0 transition-colors ${
                            expandedAccounts[account.Account] 
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          {expandedAccounts[account.Account] ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                    
                    {/* Transactions detail view */}
                    {expandedAccounts[account.Account] && (
                      <TableRow>
                        <TableCell colSpan={6} className="p-0 border-t-0 bg-gray-50">
                          <div className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-xs font-semibold flex items-center">
                                <Calendar className="h-3 w-3 mr-1 text-gray-500" />
                                Transactions for Account {account.Account}
                              </h4>
                              <Badge className="bg-blue-600 text-white hover:bg-blue-700 transition-colors text-xs">
                                {transactions.length} Transaction{transactions.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                            
                            {transactions.length > 0 ? (
                              <div className="bg-white rounded-md border overflow-x-auto shadow-sm">
                                <Table className="table-fixed w-full">
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="w-20 bg-blue-50 text-blue-800 font-semibold text-xs p-1">Date</TableHead>
                                      <TableHead className="w-16 bg-blue-50 text-blue-800 font-semibold text-xs p-1">JV#</TableHead>
                                      <TableHead className="w-12 bg-blue-50 text-blue-800 font-semibold text-xs p-1">D/C</TableHead>
                                      <TableHead className="text-right w-20 bg-blue-50 text-blue-800 font-semibold text-xs p-1">Amount</TableHead>
                                      <TableHead className="bg-blue-50 text-blue-800 font-semibold text-xs p-1">Description</TableHead>
                                      </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {transactions.map((transaction, index) => (
                                      <TableRow 
                                        key={index} 
                                        className={`hover:bg-blue-50/50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                                      >
                                        <TableCell className="font-medium py-1 text-xs">{transaction.Date}</TableCell>
                                        <TableCell className="font-medium py-1 text-xs">{transaction['JV#'] || '-'}</TableCell>
                                        <TableCell className="py-1">
                                          <Badge 
                                            className={`text-xs w-8 justify-center ${
                                              transaction['D/C'] === 'C' 
                                                ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                                            }`}
                                          >{transaction['D/C']}
                                          </Badge>
                                        </TableCell>
                                        <TableCell className="text-right font-medium py-1 text-xs">
                                          <span className={`font-semibold ${transaction['D/C'] === 'C' ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatCurrency(transaction.AED)}
                                          </span>
                                        </TableCell>
                                        <TableCell className="max-w-xs py-1 text-xs">
                                          <div className="truncate">
                                            {transaction.Description || transaction.Name || '-'}
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                  <tfoot>
                                    <tr className="bg-gray-100">
                                      <td colSpan={3} className="px-3 py-1 text-xs font-semibold text-gray-700">Transaction Summary</td>
                                      <td className="px-3 py-1 text-right text-xs">
                                        <div className="flex flex-col">
                                          <span className="text-green-600 text-xs font-medium">
                                            Credits: {formatCurrency(transactions.reduce((sum, t) => sum + (t['D/C'] === 'C' ? parseFloat(t.AED) : 0), 0))}
                                          </span>
                                          <span className="text-red-600 text-xs font-medium">
                                            Debits: {formatCurrency(transactions.reduce((sum, t) => sum + (t['D/C'] === 'D' ? parseFloat(t.AED) : 0), 0))}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="px-3 py-1"></td>
                                    </tr>
                                  </tfoot>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-6 bg-white rounded-md border">
                                <FileSpreadsheet className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                                <p className="text-gray-500 text-xs">No transactions found for this account.</p>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* If in expanded view mode, automatically expand all accounts */}
                    {viewMode === 'expanded' && !expandedAccounts[account.Account] && (
                      <TableRow className="hidden">
                        {/* Hidden row to trigger expansion effect */}
                        {toggleAccountExpanded(account)}
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                
                {/* Totals row */}
                <TableRow className="bg-gray-800 text-white font-bold">
                  <TableCell colSpan={2} className="text-white text-sm p-2">Total</TableCell>
                  <TableCell className="text-right text-white p-2">
                    <div className="flex flex-col">
                      <span className="text-green-300 text-xs">{formatCurrency(totalCredit)}</span>
                      <span className="text-xs text-gray-300 mt-0.5">Total Credits</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-white p-2">
                    <div className="flex flex-col">
                      <span className="text-red-300 text-xs">{formatCurrency(totalDebit)}</span>
                      <span className="text-xs text-gray-300 mt-0.5">Total Debits</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-white p-2">
                    <div className="flex flex-col">
                      <span className={`text-xs ${netTotal >= 0 ? "text-green-300" : "text-red-300"}`}>{formatCurrency(netTotal)}</span>
                      <span className="text-xs text-gray-300 mt-0.5">Net Balance</span>
                    </div>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 bg-gray-50">
              <FileSpreadsheet className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-700 mb-2">No Accounts Found</h3>
              <p className="text-gray-500 max-w-md mx-auto text-sm">
                {searchTerm ? 
                  `No accounts match the search term "${searchTerm}".` : 
                  "There are no accounts available for this category and month."}
              </p>
              {searchTerm && (
                <Button 
                  variant="outline" 
                  className="mt-3 text-sm"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </Button>
              )}
            </div>
          )}
        </div>
        
        {/* Footer with details */}
        <div className="text-xs text-gray-500 p-3 border-t bg-gray-50">
          <p className="flex items-center">
            <Calendar className="h-3 w-3 mr-1" /> 
            Report generated for period: <span className="font-medium ml-1">{month}</span>
          </p>
          <p className="mt-1">
            Material accounts can include supply costs, inventory changes, freight, and raw materials.
          </p>
        </div>
        
        {/* Fix Date Issues Dialog */}
        <Dialog open={fixDateDialogOpen} onOpenChange={setFixDateDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center text-base">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                Fix Date Issues
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-sm text-gray-700">
                The system has detected {dateIssuesCount} transaction(s) that may have incorrect dates.
                These transactions appear to have been created in {month} but are dated for the previous month.
              </p>
              
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <h4 className="text-sm font-medium text-yellow-800">What will be fixed:</h4>
                <ul className="mt-2 space-y-1 text-xs text-yellow-700 list-disc pl-4">
                  <li>Transactions dated on the last day of the previous month but created in {month}</li>
                  <li>Specifically affects accounts in the Materials category</li>
                  <li>Will update the dates to the first day of {month}</li>
                  <li>Profit and loss calculations will be recalculated for affected months</li>
                </ul>
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setFixDateDialogOpen(false)}
                disabled={isFixingDates}
              >
                Cancel
              </Button>
              <Button
                onClick={handleFixDateIssues}
                disabled={isFixingDates}
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                {isFixingDates ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Fixing...
                  </>
                ) : (
                  "Fix Date Issues"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default MaterialsDetailView;
